/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import SelectMissionContent from "./component/SelectMissionContent";
import "./SelectMission.css";
import { simpleFetch } from "../../restWrapper";

import { Helmet } from "react-helmet";
import { getOrganizationID, validatePlatformMatch, getTextColor, getContrastTextColor } from "../../assets/helper.js";
import { PLATFORM } from "../../config";
import { rememberOrgTitle } from "../../assets/orgTitle";

class SelectMission extends Component {
  constructor(props) {
    super(props);

    const subdomain = getOrganizationID(window.location.href).subdomain;
    const domain = getOrganizationID(window.location.href).domain;
    const isCustomDomain = getOrganizationID(window.location.href).isCustomDomain;
    const locationState = props.location && props.location.state ? props.location.state : {};
    this.state = {
      fetching: true, // Set to true initially while fetching organization data
      // To avoid inital fetching & InfiniteScroll fetching
      // At the same time
      subdomain: subdomain,
      domain: domain,
      isCustomDomain: isCustomDomain,
      missions: [],
      lastId: 0,
      hasMore: true,
      headerColor: "",
      buttonColor: "",
      logo: "",
      organizationUrl: "",
      organizationName: "", // Initialize to prevent undefined error
      showQuickPayment: locationState.isQuickPaymentFlow || false,
      amount: locationState.amount || 0,
      ...locationState // Preserve other state passed from PersonalDetails
    };
    this.handleSwitchToStandardMode = this.handleSwitchToStandardMode.bind(this);
    this.handleToggleQuickPayment = this.handleToggleQuickPayment.bind(this);
  }

  handleSwitchToStandardMode() {
    this.setState({ showQuickPayment: false });
  }

  handleToggleQuickPayment() {
    this.setState({ showQuickPayment: !this.state.showQuickPayment });
  }

  componentDidMount() {
    if (this.state.isCustomDomain) {
      // Custom domain logic
    } else if (this.state.subdomain === null || this.state.subdomain === "") {
      return false;
    }
    if (!this.state.isCustomDomain && this.state.subdomain === "www") {
      return false;
    }

    // Fetch complete organization data including payment methods
    simpleFetch("/donors/organization", "GET", {
      subDomain: this.state.isCustomDomain ? this.state.domain : this.state.subdomain,
      isCustomDomain: this.state.isCustomDomain,
    }).then((res) => {
      if (res.status !== 200) {
        // console.error("Failed to fetch organization data:", res);
        this.setState({ fetching: false });
        this.props.history.push({ pathname: "/" + this.props.language });
        return false;
      }
      
      const orgObj = res.body.organizationObj;
      rememberOrgTitle(orgObj && orgObj.organizationName);

      // Validate platform matches organization type
      const platformMatch = validatePlatformMatch(
        orgObj.organizationType,
        PLATFORM
      );

      if (!platformMatch) {
        // console.error("Cannot display payment page: platform mismatch");
        this.setState({ fetching: false });
        this.props.history.push({ pathname: "/" + this.props.language });
        return false;
      }

      // Ensure paymentMethodCode exists
      if (!orgObj.paymentMethodCode) {
        // console.warn("Organization data missing paymentMethodCode, using empty object");
        orgObj.paymentMethodCode = {};
      }
      
      // Create a complete organization object
      const plan = orgObj.currentPlan || "basic";
      const isFreePlan = plan === "free";

      // Use organization's colors if set, otherwise use plan-based defaults
      const defaultHeaderColor = isFreePlan ? "4A9FE9" : "000000";
      const defaultButtonColor = "ffffff";
      const defaultProgressColor = isFreePlan ? "4A9FE9" : "000000";

      const organization = {
        headerColor: orgObj.headerColor || defaultHeaderColor,
        buttonColor: orgObj.buttonColor || defaultButtonColor,
        progressBarColor: orgObj.progressBarColor || defaultProgressColor,
        // Store the actual org color separately for logo usage
        orgHeaderColor: orgObj.headerColor,
        orgButtonColor: orgObj.buttonColor,
        logo: orgObj.logo || "",
        organizationName: orgObj.organizationName || "Organization",
        organizationUrl: orgObj.organizationUrl || "",
        currentPlan: plan,
        paymentMethodCode: orgObj.paymentMethodCode,
        minimumPaymentAmount: orgObj.minimumPaymentAmount || 0,
        presetDonationAmounts: orgObj.presetDonationAmounts || null,
        defaultLanguage: orgObj.defaultLanguage || null,
        supportedLanguages: orgObj.supportedLanguages || [],
        autoDetectLanguage: orgObj.autoDetectLanguage !== undefined ? orgObj.autoDetectLanguage : true,
        // Include organization ID and organization View ID
        organizationId: res.body._id,
        organizationViewId: res.body.id,
        // Whether PayMe is set up — the backend reports this instead of the
        // credentials (CP-326).
        paymeConfigured: orgObj.paymeConfigured === true,
        // PayMe credentials: only an older backend still sends these, and the
        // new one ignores them in requests. Kept until CP-326 is deployed, so
        // PayMe keeps working whichever side ships first; then remove.
        clientId: orgObj.clientId,
        clientSecret: orgObj.clientSecret,
        signingKeyId: orgObj.signingKeyId,
        signingKey: orgObj.signingKey,
        enableStripeLink: orgObj.enableStripeLink !== false,
        allowedTransactionTypes: orgObj.allowedTransactionTypes || "both"
      };

      // console.log("Setting organizationId:", res.body._id);
      // console.log("Setting organizationViewId:", res.body.id);
      // console.log("Organization defaultLanguage from API:", orgObj.defaultLanguage);
      // console.log("Organization supportedLanguages from API:", orgObj.supportedLanguages);
      // console.log("Organization autoDetectLanguage from API:", orgObj.autoDetectLanguage);
      // console.log("i18n available:", !!this.props.i18n);
      // console.log("Current language before change:", this.props.i18n ? this.props.i18n.language : 'N/A');

      // Apply organization's language settings
      if (this.props.i18n) {
        const currentPlan = organization.currentPlan || '';
        const isPremiumOrPro = currentPlan === 'premium' || currentPlan === 'pro';

        const supportedLanguages = organization.supportedLanguages && organization.supportedLanguages.length > 0
          ? organization.supportedLanguages
          : (organization.defaultLanguage ? [organization.defaultLanguage] : []);

        if (supportedLanguages.length > 0) {
          // Use defaultLanguage field as the explicit default, or first supported language, or 'en' as final fallback
          const defaultLang = organization.defaultLanguage || supportedLanguages[0] || 'en';

          // For Free/Plus plans: Always use default language only (ignore auto-detect and multiple languages)
          if (!isPremiumOrPro) {
            // console.log("Free/Plus plan: Using default language only:", defaultLang);
            this.props.i18n.changeLanguage(defaultLang);
          } else {
            // For Pro/Premium plans: Respect auto-detect setting
            const autoDetect = organization.autoDetectLanguage !== false;

            if (!autoDetect) {
              // Always use default language (auto-detect disabled)
              // console.log("Using default language only (auto-detect disabled):", defaultLang);
              this.props.i18n.changeLanguage(defaultLang);
            } else {
              // Auto-detect: Use browser language if supported, otherwise default
              const currentLang = this.props.i18n.language;
              if (supportedLanguages.includes(currentLang)) {
                // console.log("Auto-detect: Current browser language is supported, keeping:", currentLang);
                // Don't change - keep browser language
              } else {
                // console.log("Auto-detect: Browser language not supported, using default:", defaultLang);
                this.props.i18n.changeLanguage(defaultLang);
              }
            }
          }
        } else {
          // No languages configured - default to English
          // console.log("No languages configured, defaulting to English");
          this.props.i18n.changeLanguage('en');
        }
      }

      this.setState({
        ...this.state,
        ...{
          headerColor: organization.headerColor,
          buttonColor: organization.buttonColor,
          progressBarColor: organization.progressBarColor,
          logo: organization.logo,
          organizationName: organization.organizationName,
          organizationUrl: organization.organizationUrl,
          currentPlan: organization.currentPlan,
          showQuickPayment: isFreePlan ? true : this.state.showQuickPayment,
          // Store the complete organization object
          organization: organization,
          // Also keep individual properties for backward compatibility
          paymentMethodCode: organization.paymentMethodCode,
          fetching: false, // Organization data loaded successfully
        },
      });
    }).catch(error => {
      // console.error("Error fetching organization data:", error);
      this.setState({ fetching: false });
      this.props.history.push({ pathname: "/" + this.props.language });
    });

  }
  render() {
    /* 
    if (this.state.fetching) {
      return (
        <this.props.RenderFullPageLoading
          loadingColor={this.state.headerColor}
        />
      );
    }
    */

    const languageObj = this.props.languageObj;

    const buttonOverrideStyle = {
      background: "#" + this.state.buttonColor,
    };

    const isFreePlan = this.state.currentPlan === "free";

    const backgroundStyle = isFreePlan
      ? {}
      : {
        background: "#" + this.state.headerColor,
      };

    const buttonOverrideTextStyle = {
      color: getTextColor(this.state.buttonColor),
    };

    const buttonOverrideGroupStyle = {
      background: "#" + this.state.buttonColor,
      color: getContrastTextColor(this.state.buttonColor, this.state.headerColor),
    };

    const textOverrideGroupStyle = {
      color: getTextColor(this.state.buttonColor),
    };

    const color = {
      headerColor: this.state.headerColor,
      buttonColor: this.state.buttonColor,
    };

    const logoStyle = {
      backgroundImage: "url('" + this.state.logo + "')",
      backgroundRepeat: "no-repeat",
      backgroundSize: "contain",
      border: "none",
    };

    const organizationName = this.state.organizationName;
    // Removed console.log from render method to prevent spam on every re-render
    // console.log(this.state.organizationUrl + " -134");

    const viewClassName = isFreePlan
      ? "SelectMission-view SelectMission-view--free"
      : "SelectMission-view";

    return (
      <div className={viewClassName} style={backgroundStyle}>
        <Helmet>
          <title>{organizationName}</title>
        </Helmet>
        <SelectMissionContent
          state={this.state}
          languageObj={languageObj}
          logoStyle={logoStyle}
          organizationName={organizationName}
          organizationUrl={this.state.organizationUrl}
          isAuth={this.props.isAuth}
          buttonOverrideTextStyle={buttonOverrideTextStyle}
          buttonOverrideStyle={buttonOverrideStyle}
          buttonOverrideGroupStyle={buttonOverrideGroupStyle}
          textOverrideGroupStyle={textOverrideGroupStyle}
          RenderFullPageLoading={this.props.RenderFullPageLoading}
          history={this.props.history}
          language={this.props.language}
          color={color}
          onToggleQuickPayment={this.handleToggleQuickPayment}
          onSwitchToStandardMode={this.handleSwitchToStandardMode}
        />
      </div>
    );
  }
}

SelectMission.propTypes = {
  language: PropTypes.string,
  languageObj: PropTypes.object,
  isAuth: PropTypes.bool,
  i18n: PropTypes.object,
  RenderFullPageLoading: PropTypes.func,
};

export default SelectMission;
